import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Download,
  FileText,
  Image as ImageIcon,
  Loader2,
  Maximize2,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  companyRequisitesApi,
  type CompanyRequisitesAsset,
} from '@/api/company-requisites';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface CompanyRequisitesAssetsSectionProps {
  requisitesId: string;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as {
      response?: { data?: { message?: string | string[] } };
    }).response;
    const message = response?.data?.message;
    if (Array.isArray(message)) return message.join(', ');
    if (message) return message;
  }
  return error instanceof Error ? error.message : fallback;
}

function formatSize(sizeBytes: number): string {
  if (sizeBytes < 1024) return `${sizeBytes} Б`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} КБ`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

function AssetMeta({ asset }: { asset: CompanyRequisitesAsset }) {
  const timestamp = asset.kind === 'LETTERHEAD_DOCX'
    ? asset.updatedAt
    : asset.createdAt;

  return (
    <div className="mt-0.5 text-xs text-muted-foreground">
      {formatSize(asset.sizeBytes)} · {formatDate(timestamp)}
      {asset.uploadedBy?.name && ` · ${asset.uploadedBy.name}`}
    </div>
  );
}

function LogoBlobImage({
  blob,
  alt,
  className,
  testId,
}: {
  blob: Blob;
  alt: string;
  className: string;
  testId?: string;
}) {
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(blob);
    const image = imageRef.current;
    if (image) image.src = objectUrl;

    return () => {
      if (image?.src === objectUrl) image.removeAttribute('src');
      URL.revokeObjectURL(objectUrl);
    };
  }, [blob]);

  return (
    <img
      ref={imageRef}
      alt={alt}
      className={className}
      draggable={false}
      data-testid={testId}
    />
  );
}

function useLogoBlob(requisitesId: string, logo: CompanyRequisitesAsset) {
  return useQuery({
    queryKey: [
      'mobile-company-requisites-logo',
      requisitesId,
      logo.id,
      logo.sha256,
    ],
    queryFn: () => companyRequisitesApi.downloadLogo(requisitesId, logo.id),
    staleTime: Infinity,
    gcTime: 60_000,
    retry: 1,
  });
}

function LogoThumbnail({
  requisitesId,
  logo,
  onOpen,
}: {
  requisitesId: string;
  logo: CompanyRequisitesAsset;
  onOpen: (trigger: HTMLButtonElement) => void;
}) {
  const previewQuery = useLogoBlob(requisitesId, logo);
  const name = logo.label || logo.originalName;

  return (
    <button
      type="button"
      className="group relative flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted/40 p-1.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:bg-accent"
      aria-label={`Просмотреть ${name}`}
      title="Открыть просмотр"
      data-testid="company-requisites-logo-preview"
      onClick={(event) => onOpen(event.currentTarget)}
    >
      {previewQuery.data ? (
        <LogoBlobImage
          blob={previewQuery.data}
          alt=""
          className="h-full w-full object-contain"
        />
      ) : previewQuery.isError ? (
        <AlertCircle className="h-6 w-6 text-destructive" />
      ) : (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      )}
      <span className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-focus-visible:opacity-100 group-active:opacity-100">
        <Maximize2 className="h-5 w-5 text-white" />
      </span>
    </button>
  );
}

function LogoPreviewDialog({
  requisitesId,
  logo,
  onClose,
  onReturnFocus,
}: {
  requisitesId: string;
  logo: CompanyRequisitesAsset;
  onClose: () => void;
  onReturnFocus: () => void;
}) {
  const previewQuery = useLogoBlob(requisitesId, logo);
  const name = logo.label || logo.originalName;

  return (
    <Dialog open onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <DialogContent
        className="z-[70] flex max-h-[92dvh] w-[calc(100vw-1.5rem)] max-w-lg flex-col gap-0 overflow-hidden rounded-xl p-0"
        data-testid="company-requisites-logo-preview-dialog"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          onReturnFocus();
        }}
      >
        <DialogHeader className="border-b border-border px-4 py-4 pr-12 text-left">
          <DialogTitle className="truncate">{name}</DialogTitle>
          <DialogDescription className="truncate">
            {logo.label ? `${logo.originalName} · ` : ''}
            {formatSize(logo.sizeBytes)}
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-64 flex-1 items-center justify-center overflow-auto bg-muted/50 p-4">
          {previewQuery.data ? (
            <LogoBlobImage
              blob={previewQuery.data}
              alt={name}
              className="max-h-[70dvh] max-w-full object-contain"
              testId="company-requisites-logo-preview-image"
            />
          ) : previewQuery.isError ? (
            <div className="flex flex-col items-center gap-3 text-center" role="alert">
              <ImageIcon className="h-10 w-10 text-destructive" />
              <p className="text-sm text-destructive">
                {errorMessage(previewQuery.error, 'Не удалось загрузить логотип')}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void previewQuery.refetch()}
              >
                <RefreshCw className="h-4 w-4" />
                Повторить
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Загрузка изображения…
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CompanyRequisitesAssetsSection({
  requisitesId,
}: CompanyRequisitesAssetsSectionProps) {
  const [downloadingAssetId, setDownloadingAssetId] = useState<string | null>(null);
  const [previewLogo, setPreviewLogo] = useState<CompanyRequisitesAsset | null>(null);
  const previewTriggerRef = useRef<HTMLButtonElement | null>(null);
  const assetsQuery = useQuery({
    queryKey: ['mobile-company-requisites-assets', requisitesId],
    queryFn: () => companyRequisitesApi.getAssets(requisitesId),
    retry: 1,
  });

  const downloadLetterhead = async (asset: CompanyRequisitesAsset) => {
    setDownloadingAssetId(asset.id);
    try {
      const blob = await companyRequisitesApi.downloadLetterhead(requisitesId);
      downloadBlob(blob, asset.originalName);
    } catch (error) {
      toast.error(errorMessage(error, 'Не удалось скачать фирменный бланк'));
    } finally {
      setDownloadingAssetId(null);
    }
  };

  const downloadLogo = async (asset: CompanyRequisitesAsset) => {
    setDownloadingAssetId(asset.id);
    try {
      const blob = await companyRequisitesApi.downloadLogo(requisitesId, asset.id);
      downloadBlob(blob, asset.originalName);
    } catch (error) {
      toast.error(errorMessage(error, 'Не удалось скачать логотип'));
    } finally {
      setDownloadingAssetId(null);
    }
  };

  if (assetsQuery.isLoading) {
    return (
      <section
        className="rounded-lg border border-border bg-card p-3"
        data-testid="company-requisites-assets-section"
      >
        <h3 className="mb-3 text-sm font-semibold">Фирменные файлы</h3>
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Загрузка файлов…
        </div>
      </section>
    );
  }

  if (assetsQuery.isError) {
    return (
      <section
        className="rounded-lg border border-destructive/40 bg-card p-3"
        data-testid="company-requisites-assets-section"
      >
        <h3 className="mb-3 text-sm font-semibold">Фирменные файлы</h3>
        <div className="flex flex-col items-start gap-3" role="alert">
          <p className="text-sm text-destructive">
            {errorMessage(assetsQuery.error, 'Не удалось загрузить список файлов')}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void assetsQuery.refetch()}
          >
            <RefreshCw className="h-4 w-4" />
            Повторить
          </Button>
        </div>
      </section>
    );
  }

  const letterhead = assetsQuery.data?.letterhead ?? null;
  const logos = assetsQuery.data?.logos ?? [];

  return (
    <section
      className="space-y-5 rounded-lg border border-border bg-card p-3"
      data-testid="company-requisites-assets-section"
    >
      <h3 className="text-sm font-semibold">Фирменные файлы</h3>

      <div>
        <h4 className="mb-2 text-sm font-medium">Фирменный бланк</h4>
        {letterhead ? (
          <div
            className="flex items-center gap-3 rounded-lg border border-border p-3"
            data-testid="company-requisites-letterhead"
          >
            <FileText className="h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{letterhead.originalName}</div>
              <AssetMeta asset={letterhead} />
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Скачать фирменный бланк"
              title="Скачать фирменный бланк"
              disabled={downloadingAssetId === letterhead.id}
              onClick={() => void downloadLetterhead(letterhead)}
            >
              {downloadingAssetId === letterhead.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3 text-sm text-muted-foreground">
            Для этой версии реквизитов фирменный бланк не задан.
          </div>
        )}
      </div>

      <div>
        <h4 className="mb-2 text-sm font-medium">Файлы логотипов</h4>
        {logos.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3 text-sm text-muted-foreground">
            Файлы логотипов не добавлены.
          </div>
        ) : (
          <div className="space-y-2">
            {logos.map((logo) => {
              const name = logo.label || logo.originalName;
              return (
                <div
                  key={logo.id}
                  className="flex items-center gap-3 rounded-lg border border-border p-3"
                  data-testid="company-requisites-logo-row"
                >
                  <LogoThumbnail
                    requisitesId={requisitesId}
                    logo={logo}
                    onOpen={(trigger) => {
                      previewTriggerRef.current = trigger;
                      setPreviewLogo(logo);
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{name}</div>
                    {logo.label && (
                      <div className="truncate text-xs text-muted-foreground">
                        {logo.originalName}
                      </div>
                    )}
                    <AssetMeta asset={logo} />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={`Скачать ${name}`}
                    title="Скачать SVG"
                    disabled={downloadingAssetId === logo.id}
                    onClick={() => void downloadLogo(logo)}
                  >
                    {downloadingAssetId === logo.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {previewLogo && (
        <LogoPreviewDialog
          requisitesId={requisitesId}
          logo={previewLogo}
          onClose={() => setPreviewLogo(null)}
          onReturnFocus={() => previewTriggerRef.current?.focus()}
        />
      )}
    </section>
  );
}
